import { Loader2 } from "lucide-react";

const BTN = {
  primary: "bg-[#0F5E59] text-white hover:bg-[#0B4B47] border border-[#0F5E59] shadow-sm",
  secondary: "bg-white text-[#1F2526] border border-[#CDD3D1] hover:bg-[#F1F3F1]",
  ghost: "bg-transparent text-[#3B4446] border border-transparent hover:bg-[#ECEFEC]",
  danger: "bg-white text-[#9B2C2C] border border-[#E5C4C4] hover:bg-[#FBEFEF]",
};

export function Btn({ variant = "secondary", size = "md", loading, className = "", children, ...props }) {
  const sz = size === "sm" ? "h-8 px-3 text-[13px]" : "h-9 px-4 text-sm";
  return (
    <button
      className={`inline-flex items-center justify-center gap-1.5 rounded-md font-medium transition-colors duration-150 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap ${sz} ${BTN[variant]} ${className}`}
      disabled={loading || props.disabled}
      {...props}
    >
      {loading && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
      {children}
    </button>
  );
}

const TONES = {
  neutral: "bg-[#EEF1EF] text-[#4A5456] border-[#D5DBD8]",
  teal: "bg-[#E3F0EE] text-[#0F5E59] border-[#B9D8D4]",
  amber: "bg-[#FBF1DC] text-[#8A5A0B] border-[#EED9A8]",
  red: "bg-[#FBECEC] text-[#9B2C2C] border-[#EDC9C9]",
  blue: "bg-[#E8EFF8] text-[#284B7A] border-[#C9D7EB]",
  plum: "bg-[#F2ECF5] text-[#5D3A6E] border-[#DCCDE4]",
  green: "bg-[#E6F2E8] text-[#25603A] border-[#BFDDC6]",
  outline: "bg-white text-[#4A5456] border-[#CDD3D1]",
};

export function Badge({ tone = "neutral", className = "", children, ...props }) {
  return (
    <span
      className={`inline-flex items-center gap-1 rounded border px-1.5 py-[1px] text-[11.5px] font-medium leading-5 whitespace-nowrap ${TONES[tone]} ${className}`}
      {...props}
    >
      {children}
    </span>
  );
}

export const ROLE_TONE = { subcontractor: "teal", gc: "blue", architect: "plum" };

export function Field({ label, hint, children, htmlFor }) {
  return (
    <label className="block space-y-1" htmlFor={htmlFor}>
      <span className="text-[12.5px] font-medium text-[#3B4446]">{label}</span>
      {children}
      {hint && <span className="block text-xs text-[#6B7577]">{hint}</span>}
    </label>
  );
}

export const inputCls =
  "w-full h-9 rounded-md border border-[#CDD3D1] bg-white px-3 text-sm text-[#1F2526] placeholder:text-[#9AA3A4] focus:outline-none focus:ring-2 focus:ring-[#0F5E59]/30 focus:border-[#0F5E59]";

export function Input(props) {
  return <input {...props} className={`${inputCls} ${props.className || ""}`} />;
}

export function Select({ children, ...props }) {
  return (
    <select {...props} className={`${inputCls} pr-8 ${props.className || ""}`}>
      {children}
    </select>
  );
}

export function Eyebrow({ children, className = "" }) {
  return <div className={`text-[11px] font-semibold uppercase tracking-[0.08em] text-[#6B7577] ${className}`}>{children}</div>;
}

export function Panel({ title, action, children, className = "", ...props }) {
  return (
    <section className={`rounded-lg border border-[#DDE2DF] bg-white ${className}`} {...props}>
      {(title || action) && (
        <header className="flex items-center justify-between gap-3 border-b border-[#E7EBE8] px-4 py-2.5">
          <h2 className="text-[14px] font-semibold text-[#1F2526]">{title}</h2>
          {action}
        </header>
      )}
      {children}
    </section>
  );
}

export function EmptyState({ icon: Icon, title, children, action, ...props }) {
  return (
    <div className="flex flex-col items-start gap-2 px-5 py-8" {...props}>
      {Icon && <Icon className="h-5 w-5 text-[#8A9496]" strokeWidth={1.6} />}
      <div className="text-sm font-medium text-[#1F2526]">{title}</div>
      {children && <div className="max-w-xl text-[13px] text-[#5B6567]">{children}</div>}
      {action && <div className="pt-1">{action}</div>}
    </div>
  );
}

export function ErrorText({ children, ...props }) {
  if (!children) return null;
  return (
    <div role="alert" className="rounded-md border border-[#EDC9C9] bg-[#FBECEC] px-3 py-2 text-[13px] text-[#8A2626]" {...props}>
      {children}
    </div>
  );
}

export function Spinner({ label = "Loading" }) {
  return (
    <div className="flex items-center gap-2 px-6 py-10 text-sm text-[#5B6567]" data-testid="loading-indicator">
      <Loader2 className="h-4 w-4 animate-spin" /> {label}
    </div>
  );
}

export function Avatar({ name, tone = "neutral", ...props }) {
  const initials = (name || "?").split(" ").map((p) => p[0]).slice(0, 2).join("").toUpperCase();
  return (
    <span
      className={`inline-flex h-7 w-7 items-center justify-center rounded-full border text-[11px] font-semibold ${TONES[tone]}`}
      title={name}
      {...props}
    >
      {initials}
    </span>
  );
}

export function DoorMark({ className = "h-5 w-5" }) {
  return (
    <svg viewBox="0 0 24 24" className={className} fill="none" stroke="currentColor" strokeWidth="1.7" aria-hidden="true">
      <path d="M5 21V4.5a1 1 0 0 1 1-1h12a1 1 0 0 1 1 1V21" />
      <path d="M3 21h18" />
      <path d="M8 21V6.5h8V21" />
      <circle cx="14.2" cy="13.5" r="0.9" fill="currentColor" stroke="none" />
    </svg>
  );
}
